import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from "@/components/ui/resizable";
import { PromptTraceViewer } from "./PromptTraceViewer";
import { RunSidebar } from "./RunSidebar";
import { convertScoreToFive } from "@/lib/utils";
import { ensureArray, formatDate } from "@/lib/analysis-constants";
import { useAnalysisPdfExport } from "@/hooks/useAnalysisPdfExport";
import { toast } from "@/hooks/use-toast";
import { CheckCircle, AlertCircle, XCircle, Target, Award, TrendingUp, Eye, RefreshCw, Building2, Calendar, DollarSign, FileText, ThumbsUp, ThumbsDown, AlertTriangle, Trash2, Lightbulb, MessageCircle, Loader2, FileDown, Cpu, Star, EyeOff } from "lucide-react";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import api from "@/services/api";
import { analysesService } from "@/services";
interface Analyse {
  id: string;
  depot_id: string;
  ao_id: string;
  resultat_json: any;
  overall_score: number;
  decision: 'shortlist' | 'consider' | 'reject';
  rating?: string;
  comment?: string;
  is_excluded?: boolean;
  updated_at?: string;
  analysis_mode?: string;
  model_used?: string;
  tokens_in: number;
  tokens_out: number;
  cost_estimate: number;
  created_at: string;
  depots: {
    candidats: {
      nom: string;
      societes: {
        nom: string;
      };
    };
    appels_offres: {
      titre: string;
      reference: string;
    };
    texte_extrait: string;
    fichier_file_id: string;
  };
}
interface Question {
  question: string;
  critere_associe: string;
  objectif: string;
}
interface AnalysisViewerProps {
  isOpen: boolean;
  onClose: () => void;
  analysis: Analyse | null;
  onViewCV?: (analysis: Analyse) => void;
  onReanalyze?: (depotId: string) => void;
  onDelete?: (analysisId: string) => void;
  onSelectAnalysis?: (analysisId: string) => void;
  onUpdateAnalysis?: () => void;
  isReanalyzing?: boolean;
  depotExtractionCost?: number | null;
  isFirstRun?: boolean;
}
export const AnalysisViewer = ({
  isOpen,
  onClose,
  analysis,
  onViewCV,
  onReanalyze,
  onDelete,
  onSelectAnalysis,
  onUpdateAnalysis,
  isReanalyzing = false,
  depotExtractionCost,
  isFirstRun = false,
}: AnalysisViewerProps) => {
  const [questions, setQuestions] = useState<Question[]>([]);
  const [isLoadingQuestions, setIsLoadingQuestions] = useState(false);
  const [questionsError, setQuestionsError] = useState<string | null>(null);
  const [localRating, setLocalRating] = useState<string | undefined>(undefined);
  const [localComment, setLocalComment] = useState<string>("");
  const [localExcluded, setLocalExcluded] = useState<boolean>(false);
  const [savingComment, setSavingComment] = useState(false);

  // Sync local state when analysis changes
  useEffect(() => {
    if (analysis) {
      setLocalRating(analysis.rating || undefined);
      setLocalComment(analysis.comment || "");
      setLocalExcluded(analysis.is_excluded || false);
    }
  }, [analysis?.id]);

  const handleRatingChange = async (value: string) => {
    if (!analysis) return;
    const newRating = value === localRating ? undefined : value;
    setLocalRating(newRating || undefined);
    try {
      await analysesService.update(analysis.id, { rating: (newRating || null) as any });
      toast({ title: "Rating mis à jour", description: newRating ? `Rating: ${newRating}` : "Rating supprimé" });
      onUpdateAnalysis?.();
    } catch {
      toast({ title: "Erreur", description: "Impossible de mettre à jour le rating", variant: "destructive" });
      setLocalRating(analysis.rating || undefined);
    }
  };

  const handleSaveComment = async () => {
    if (!analysis) return;
    setSavingComment(true);
    try {
      await analysesService.update(analysis.id, { comment: localComment || null });
      toast({ title: "Commentaire sauvegardé" });
      onUpdateAnalysis?.();
    } catch {
      toast({ title: "Erreur", description: "Impossible de sauvegarder le commentaire", variant: "destructive" });
    } finally {
      setSavingComment(false);
    }
  };

  const handleExclusionChange = async (checked: boolean) => {
    if (!analysis) return;
    setLocalExcluded(checked);
    try {
      await analysesService.update(analysis.id, { isExcluded: checked });
      toast({ title: checked ? "Analyse exclue" : "Analyse réintégrée" });
      onUpdateAnalysis?.();
    } catch {
      toast({ title: "Erreur", description: "Impossible de modifier l'exclusion", variant: "destructive" });
      setLocalExcluded(!checked);
    }
  };
  const {
    exportAnalysisToPdf
  } = useAnalysisPdfExport();
  if (!analysis) return null;
  const handleExportPdf = () => {
    exportAnalysisToPdf(analysis as any, questions);
    toast({
      title: "Export PDF",
      description: "Le PDF de l'analyse a été généré avec succès."
    });
  };
  const getDecisionBadge = (decision: string, score: number) => {
    const formattedScore = Number(score).toFixed(1);
    const fiveScaleScore = convertScoreToFive(score);
    switch (decision) {
      case 'shortlist':
        return <Badge className="bg-success text-success-foreground">Shortlist ({formattedScore}/100 • {fiveScaleScore}/5)</Badge>;
      case 'consider':
        return <Badge className="bg-warning text-warning-foreground">À considérer ({formattedScore}/100 • {fiveScaleScore}/5)</Badge>;
      case 'reject':
        return <Badge variant="destructive">Rejeté ({formattedScore}/100 • {fiveScaleScore}/5)</Badge>;
      default:
        return <Badge variant="outline">Inconnu ({formattedScore}/100 • {fiveScaleScore}/5)</Badge>;
    }
  };
  const getDecisionIcon = (decision: string) => {
    switch (decision) {
      case 'shortlist':
        return <CheckCircle className="h-5 w-5 text-success" />;
      case 'consider':
        return <AlertCircle className="h-5 w-5 text-warning" />;
      case 'reject':
        return <XCircle className="h-5 w-5 text-destructive" />;
      default:
        return <TrendingUp className="h-5 w-5 text-muted-foreground" />;
    }
  };
  const qualityReviewIndex = analysis.resultat_json?.guardrails?.quality_review_index
    || analysis.resultat_json?.analysis_metadata?.quality_review_index;
  const getQriBadgeClass = (level?: string) => {
    switch (level) {
      case "high":
        return "bg-success text-success-foreground";
      case "medium":
        return "bg-warning text-warning-foreground";
      case "low":
        return "bg-destructive text-destructive-foreground";
      default:
        return "bg-muted text-muted-foreground";
    }
  };
  const renderCriteriaAnalysis = () => {
    if (analysis.resultat_json.criteria_analyses) {
      return analysis.resultat_json.criteria_analyses.map((criteria: any, index: number) => <Card key={index} className="border-l-4 border-l-primary">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center justify-between">
              <div className="flex items-center flex-col items-start">
                <div className="flex items-center">
                  <Target className="h-5 w-5 mr-2 text-primary" />
                  {criteria.criterion_name}
                </div>
                {criteria.evaluation_name && <span className="text-sm text-muted-foreground ml-7">
                    Évaluation: {criteria.evaluation_name}
                  </span>}
              </div>
              <Badge variant="outline" className="flex flex-col items-center gap-0.5 leading-tight text-xs">
                <span className="font-semibold text-sm">{criteria.criterion_score || 0}/100</span>
                <span className="text-muted-foreground">{convertScoreToFive(criteria.criterion_score || 0)}/5</span>
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Score</span>
                <span className="font-medium">{criteria.criterion_score || 0}/100</span>
              </div>
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>Note (0-5)</span>
                <span>{convertScoreToFive(criteria.criterion_score || 0)}/5</span>
              </div>
              <Progress value={criteria.criterion_score || 0} className="h-2" />
              <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
                <div className="space-y-1">
                  <span>Évaluation: {criteria.evaluation_weight || 100}%</span>
                  <span>Critère: {criteria.criterion_weight || criteria.weighting}%</span>
                </div>
                <div className="space-y-1">
                  <span>Pondération combinée: {criteria.combined_weight || ((criteria.evaluation_weight || 100) * (criteria.criterion_weight || criteria.weighting) / 100).toFixed(1)}%</span>
                  <span className="font-medium">Score pondéré: {criteria.weighted_score || ((criteria.criterion_score || 0) * (criteria.combined_weight || criteria.weighting) / 100).toFixed(1)}</span>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
                <span>Confiance: {(criteria.confidence || "medium").toUpperCase()}</span>
                <span>
                  Extraits de preuve: {ensureArray(criteria.evidence_spans).length}
                </span>
              </div>
            </div>

            {criteria.found_evidence && <div className="space-y-2">
                <h4 className="font-medium text-sm flex items-center">
                  <FileText className="h-4 w-4 mr-1" />
                  Éléments trouvés
                </h4>
                <p className="text-sm bg-muted/50 p-3 rounded-md">
                  {criteria.found_evidence}
                </p>
              </div>}

            {criteria.detailed_analysis && <div className="space-y-2">
                <h4 className="font-medium text-sm">Analyse détaillée</h4>
                <p className="text-sm text-muted-foreground">
                  {criteria.detailed_analysis}
                </p>
              </div>}

            {criteria.evidence_spans && ensureArray(criteria.evidence_spans).length > 0 && <div className="space-y-2">
                <h4 className="font-medium text-sm">Extraits de preuve (evidence_spans)</h4>
                <ul className="space-y-1">
                  {ensureArray(criteria.evidence_spans).map((span: string, idx: number) => <li key={idx} className="text-xs bg-muted/40 border rounded p-2 font-mono">
                      {span}
                    </li>)}
                </ul>
              </div>}

            {criteria.missing_information && ensureArray(criteria.missing_information).length > 0 && <div className="space-y-2">
                <h4 className="font-medium text-sm flex items-center">
                  <AlertTriangle className="h-4 w-4 mr-1 text-warning" />
                  Informations manquantes
                </h4>
                <ul className="space-y-1">
                  {ensureArray(criteria.missing_information).map((item: string, idx: number) => <li key={idx} className="text-xs text-muted-foreground flex items-start">
                      <span className="mr-2">•</span>
                      {item}
                    </li>)}
                </ul>
              </div>}

            {criteria.risk_flags && ensureArray(criteria.risk_flags).length > 0 && <div className="space-y-2">
                <h4 className="font-medium text-sm">Alertes de qualité</h4>
                <div className="flex flex-wrap gap-2">
                  {ensureArray(criteria.risk_flags).map((flag: string, idx: number) => <Badge key={idx} variant="secondary" className="text-[10px]">
                      {flag}
                    </Badge>)}
                </div>
              </div>}

            {criteria.strengths && criteria.strengths.length > 0 && <div className="space-y-2">
                <h4 className="font-medium text-sm flex items-center text-success">
                  <ThumbsUp className="h-4 w-4 mr-1" />
                  Points forts
                </h4>
                <ul className="space-y-1">
                  {ensureArray(criteria.strengths).map((strength: string, idx: number) => <li key={idx} className="text-sm text-success flex items-start">
                      <span className="text-success mr-2">•</span>
                      {strength}
                    </li>)}
                </ul>
              </div>}

            {criteria.weaknesses && criteria.weaknesses.length > 0 && <div className="space-y-2">
                <h4 className="font-medium text-sm flex items-center text-destructive">
                  <ThumbsDown className="h-4 w-4 mr-1" />
                  Points faibles
                </h4>
                <ul className="space-y-1">
                  {ensureArray(criteria.weaknesses).map((weakness: string, idx: number) => <li key={idx} className="text-sm text-destructive flex items-start">
                      <span className="text-destructive mr-2">•</span>
                      {weakness}
                    </li>)}
                </ul>
              </div>}

            {criteria.recommendations && criteria.recommendations.length > 0 && <div className="space-y-2">
                <h4 className="font-medium text-sm flex items-center text-warning">
                  <Lightbulb className="h-4 w-4 mr-1" />
                  Recommandations
                </h4>
                <ul className="space-y-1">
                  {ensureArray(criteria.recommendations).map((recommendation: string, idx: number) => <li key={idx} className="text-sm bg-warning/10 text-black p-2 rounded-md border border-warning/20 flex items-start">
                      <span className="text-warning mr-2">•</span>
                      {recommendation}
                    </li>)}
                </ul>
              </div>}
          </CardContent>
        </Card>);
    }

    // Aucune donnée d'analyse disponible
    return <div className="text-center p-8">
        <AlertTriangle className="h-12 w-12 text-warning mx-auto mb-4" />
        <h3 className="text-lg font-medium text-foreground mb-2">Aucune analyse détaillée disponible</h3>
        <p className="text-muted-foreground">
          Cette analyse ne contient pas de données de critères détaillées.
          Relancez l'analyse pour obtenir des résultats structurés.
        </p>
      </div>;
  };
  const extractFilename = (filePath?: string) => {
    if (!filePath) return 'Fichier inconnu';
    const parts = filePath.split('/');
    const filename = parts[parts.length - 1];
    // Extraire seulement le nom du fichier après le préfixe numérique (ex: 175886952678G-CV_1.pdf -> CV_1.pdf)
    const match = filename.match(/\d+[A-Z]?-(.+)/);
    return match ? match[1] : filename;
  };
  const generateQuestions = async () => {
    if (!analysis) return;
    setIsLoadingQuestions(true);
    setQuestionsError(null);
    try {
      // Appel API pour générer les questions
      const { data: response } = await api.post('/analyses/generate-questions', {
        analysisId: analysis.id,
        aoId: analysis.ao_id,
        candidateName: analysis.depots?.candidats?.nom || 'Candidat'
      });

      const questionsData = response?.data;
      if (!questionsData || !questionsData.questions) {
        throw new Error('Réponse invalide du service de génération de questions');
      }

      const validQuestions = (questionsData.questions || []).filter((q: any) =>
        q && typeof q === 'object' && q.question && typeof q.question === 'string'
      );
      if (validQuestions.length === 0) {
        throw new Error('Aucune question valide n\'a pu être générée');
      }
      setQuestions(validQuestions);
    } catch (error) {
      console.error('Erreur lors de la génération des questions:', error);
      setQuestionsError(error instanceof Error ? error.message : 'Erreur inconnue');
    } finally {
      setIsLoadingQuestions(false);
    }
  };
  const renderQuestionsTab = () => {
    if (questions.length === 0 && !isLoadingQuestions && !questionsError) {
      return <div className="text-center p-8">
          <MessageCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">Questions d'entretien recommandées</h3>
          <p className="text-muted-foreground mb-4">
            Générez automatiquement 10 questions pertinentes basées sur les critères d'évaluation et l'analyse du CV.
          </p>
          <Button onClick={generateQuestions} disabled={isLoadingQuestions}>
            <MessageCircle className="h-4 w-4 mr-2" />
            Générer les questions
          </Button>
        </div>;
    }
    if (isLoadingQuestions) {
      return <div className="text-center p-8">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Génération des questions en cours...</p>
        </div>;
    }
    if (questionsError) {
      return <div className="text-center p-8">
          <AlertTriangle className="h-12 w-12 text-destructive mx-auto mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">Erreur</h3>
          <p className="text-muted-foreground mb-4">{questionsError}</p>
          <Button onClick={generateQuestions} variant="outline">
            <RefreshCw className="h-4 w-4 mr-2" />
            Réessayer
          </Button>
        </div>;
    }
    return <div className="space-y-4">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-medium">Questions d'entretien recommandées</h3>
          <Button onClick={generateQuestions} variant="outline" size="sm" disabled={isLoadingQuestions}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Régénérer
          </Button>
        </div>
        
        <div className="grid gap-4">
          {questions.map((question, index) => <Card key={index} className="border-l-4 border-l-primary/50">
              <CardContent className="pt-4">
                <div className="space-y-3">
                  <div className="flex items-start justify-between">
                    <Badge variant="outline" className="mb-2">
                      Question {index + 1}
                    </Badge>
                    <Badge variant="secondary" className="text-xs">
                      {question.critere_associe}
                    </Badge>
                  </div>
                  
                  <h4 className="font-medium text-base leading-relaxed">
                    {question.question}
                  </h4>
                  
                  <div className="flex items-start space-x-2 text-sm text-muted-foreground bg-muted/30 p-3 rounded-md">
                    <Target className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <span>{question.objectif}</span>
                  </div>
                </div>
              </CardContent>
            </Card>)}
        </div>
      </div>;
  };
  return <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-7xl h-[90vh] flex flex-col p-0">
        <DialogHeader className="flex-shrink-0 px-6 pt-6 pb-2">
          <DialogTitle className="flex items-center space-x-3">
            {getDecisionIcon(analysis.decision)}
            <span>Analyse de {extractFilename(analysis.depots.fichier_file_id)}</span>
            {getDecisionBadge(analysis.decision, analysis.overall_score)}
          </DialogTitle>
          <DialogDescription>
            <div className="flex items-center flex-wrap gap-x-4 gap-y-1 text-sm">
              <span className="flex items-center">
                <Building2 className="h-4 w-4 mr-1" />
                {analysis.depots.candidats.societes.nom}
              </span>
              <span className="flex items-center">
                <FileText className="h-4 w-4 mr-1" />
                {analysis.depots.appels_offres.titre}
              </span>
              <span className="flex items-center">
                <Calendar className="h-4 w-4 mr-1" />
                {formatDate(analysis.created_at)}
              </span>
              <span className="flex items-center">
                <Cpu className="h-4 w-4 mr-1" />
                <Badge variant="secondary" className="text-xs px-2 py-0">
                  {analysis.resultat_json?.analysis_metadata?.model_used || 'Modele non specifie'}
                </Badge>
              </span>
            </div>
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 min-h-0">
          <ResizablePanelGroup direction="horizontal">
            {/* Left panel: Run sidebar */}
            <ResizablePanel defaultSize={22} minSize={15} maxSize={35}>
              <RunSidebar
                depotId={analysis.depot_id}
                currentAnalysisId={analysis.id}
                onSelectAnalysis={onSelectAnalysis}
                depotExtractionCost={depotExtractionCost}
              />
            </ResizablePanel>

            <ResizableHandle withHandle />

            {/* Right panel: Tabs content */}
            <ResizablePanel defaultSize={78}>
              <div className="h-full flex flex-col px-6 pb-2">
                <Tabs defaultValue="overview" className="h-full flex flex-col">
                  <TabsList className="grid w-full grid-cols-4 flex-shrink-0">
                    <TabsTrigger value="overview">Vue d'ensemble</TabsTrigger>
                    <TabsTrigger value="criteria">Analyse detaillee</TabsTrigger>
                    <TabsTrigger value="questions">Questions</TabsTrigger>
                    <TabsTrigger value="metadata">Metadonnees</TabsTrigger>
                  </TabsList>

                  <div className="flex-1 min-h-0 mt-4">
                    <TabsContent value="overview" className="h-full">
                      <ScrollArea className="h-full">
                        <div className="space-y-6 pr-4">
                          {/* Score global */}
                          <Card className="border-2 border-primary/20">
                            <CardHeader>
                              <CardTitle className="flex items-center justify-between">
                                <span className="flex items-center">
                                  <TrendingUp className="h-5 w-5 mr-2" />
                                  Score Global
                                </span>
                                <div className="text-right">
                                  <div className="text-3xl font-bold">{analysis.overall_score}/100</div>
                                  <div className="text-sm font-medium text-muted-foreground">
                                    Note {convertScoreToFive(analysis.overall_score)}/5
                                  </div>
                                </div>
                              </CardTitle>
                            </CardHeader>
                            <CardContent>
                              <Progress value={analysis.overall_score} className="h-3 mb-4" />
                              <div className="grid grid-cols-2 gap-4 text-sm">
                                <div className="space-y-1">
                                  <div className="text-muted-foreground">Decision</div>
                                  <div className="font-medium">
                                    {analysis.decision === 'shortlist' ? 'Shortlist' : analysis.decision === 'consider' ? 'A considerer' : 'Rejete'}
                                  </div>
                                </div>
                                <div className="space-y-1">
                                  <div className="text-muted-foreground">Seuils</div>
                                  <div className="text-xs">
                                    Shortlist: {analysis.resultat_json.analysis_metadata?.thresholds?.shortlist || 75}% |
                                    Considerer: {analysis.resultat_json.analysis_metadata?.thresholds?.consider || 60}%
                                  </div>
                                </div>
                              </div>
                            </CardContent>
                          </Card>

                          {/* Resume des criteres */}
                          {analysis.resultat_json.criteria_analyses ? <div className="grid gap-4">
                              {analysis.resultat_json.criteria_analyses.map((criteria: any, index: number) => <Card key={index} className="border-l-4 border-l-primary/50">
                                  <CardContent className="pt-4">
                                    <div className="flex items-center justify-between">
                                      <div>
                                        <h3 className="font-medium">{criteria.criterion_name}</h3>
                                        <p className="text-sm text-muted-foreground">
                                          Ponderation: {criteria.criterion_weight || criteria.weighting || 0}%
                                        </p>
                                      </div>
                                      <div className="text-right space-y-1">
                                        <div className="text-2xl font-bold">{criteria.criterion_score || 0}/100</div>
                                        <div className="text-xs font-medium text-muted-foreground">
                                          Note {convertScoreToFive(criteria.criterion_score || 0)}/5
                                        </div>
                                        <Progress value={criteria.criterion_score || 0} className="w-20 h-2" />
                                      </div>
                                    </div>
                                  </CardContent>
                                </Card>)}
                            </div> : <div className="grid gap-4 md:grid-cols-2">
                              <Card>
                                <CardContent className="pt-4">
                                  <div className="flex items-center justify-between">
                                    <div>
                                      <h3 className="font-medium flex items-center">
                                        <Target className="h-4 w-4 mr-2" />
                                        Competences
                                      </h3>
                                    </div>
                                    <div className="text-right space-y-1">
                                      <div className="text-xl font-bold">
                                        {analysis.resultat_json.competencies_score || analysis.resultat_json.skills_score || 0}/100
                                      </div>
                                      <div className="text-xs font-medium text-muted-foreground">
                                        Note {convertScoreToFive(analysis.resultat_json.competencies_score || analysis.resultat_json.skills_score || 0)}/5
                                      </div>
                                    </div>
                                  </div>
                                </CardContent>
                              </Card>
                              <Card>
                                <CardContent className="pt-4">
                                  <div className="flex items-center justify-between">
                                    <div>
                                      <h3 className="font-medium flex items-center">
                                        <Award className="h-4 w-4 mr-2" />
                                        Certifications
                                      </h3>
                                    </div>
                                    <div className="text-right space-y-1">
                                      <div className="text-xl font-bold">
                                        {analysis.resultat_json.certifications_score || analysis.resultat_json.certs_score || 0}/100
                                      </div>
                                      <div className="text-xs font-medium text-muted-foreground">
                                        Note {convertScoreToFive(analysis.resultat_json.certifications_score || analysis.resultat_json.certs_score || 0)}/5
                                      </div>
                                    </div>
                                  </div>
                                </CardContent>
                              </Card>
                            </div>}

                          {/* Annotation manuelle */}
                          <Card className="border-2 border-dashed">
                            <CardHeader>
                              <CardTitle className="flex items-center text-base">
                                <Star className="h-5 w-5 mr-2" />
                                Annotation manuelle
                              </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                              {/* Rating */}
                              <div className="space-y-2">
                                <Label className="text-sm font-medium">Rating</Label>
                                <ToggleGroup
                                  type="single"
                                  value={localRating || ""}
                                  onValueChange={handleRatingChange}
                                  className="justify-start"
                                >
                                  <ToggleGroupItem value="A" className="data-[state=on]:bg-success data-[state=on]:text-success-foreground">
                                    A
                                  </ToggleGroupItem>
                                  <ToggleGroupItem value="B" className="data-[state=on]:bg-warning data-[state=on]:text-warning-foreground">
                                    B
                                  </ToggleGroupItem>
                                  <ToggleGroupItem value="C" className="data-[state=on]:bg-destructive data-[state=on]:text-destructive-foreground">
                                    C
                                  </ToggleGroupItem>
                                </ToggleGroup>
                              </div>

                              {/* Comment */}
                              <div className="space-y-2">
                                <Label className="text-sm font-medium">Commentaire</Label>
                                <Textarea
                                  value={localComment}
                                  onChange={(e) => setLocalComment(e.target.value.slice(0, 400))}
                                  placeholder="Ajouter un commentaire..."
                                  rows={3}
                                  className="resize-none"
                                />
                                <div className="flex items-center justify-between">
                                  <span className="text-xs text-muted-foreground">
                                    {localComment.length}/400
                                  </span>
                                  <Button
                                    size="sm"
                                    onClick={handleSaveComment}
                                    disabled={savingComment}
                                  >
                                    {savingComment ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
                                    Sauvegarder
                                  </Button>
                                </div>
                              </div>

                              {/* Exclusion */}
                              <div className="flex items-center space-x-3">
                                <Switch
                                  checked={localExcluded}
                                  onCheckedChange={handleExclusionChange}
                                />
                                <Label className="text-sm flex items-center">
                                  <EyeOff className="h-4 w-4 mr-1" />
                                  Exclure cette analyse
                                </Label>
                              </div>
                            </CardContent>
                          </Card>
                        </div>
                      </ScrollArea>
                    </TabsContent>

                    <TabsContent value="criteria" className="h-full">
                      <ScrollArea className="h-full">
                        <div className="space-y-6 pr-4">
                          {renderCriteriaAnalysis()}
                        </div>
                      </ScrollArea>
                    </TabsContent>

                    <TabsContent value="questions" className="h-full">
                      <ScrollArea className="h-full">
                        <div className="pr-4">
                          {renderQuestionsTab()}
                        </div>
                      </ScrollArea>
                    </TabsContent>

                    <TabsContent value="metadata" className="h-full">
                      <ScrollArea className="h-full">
                        <div className="space-y-4 pr-4">
                          {/* Modele utilise */}
                          <Card>
                            <CardHeader>
                              <CardTitle className="flex items-center">
                                <Cpu className="h-5 w-5 mr-2" />
                                Modele IA utilise
                              </CardTitle>
                            </CardHeader>
                            <CardContent>
                              <div className="flex items-center space-x-3">
                                <Badge variant="secondary" className="text-sm px-3 py-1">
                                  {analysis.resultat_json.analysis_metadata?.model_used || 'Non specifie'}
                                </Badge>
                                {analysis.resultat_json.analysis_metadata?.model_used && (
                                  <span className="text-sm text-muted-foreground">
                                    {analysis.resultat_json.analysis_metadata.model_used.includes('gemini-3-flash') && 'Gemini 3 Flash'}
                                    {analysis.resultat_json.analysis_metadata.model_used.includes('gemini-3-pro') && 'Gemini 3 Pro'}
                                    {analysis.resultat_json.analysis_metadata.model_used.includes('gemini-2.5-flash') && 'Gemini 2.5 Flash'}
                                    {analysis.resultat_json.analysis_metadata.model_used.includes('gemini-2.5-pro') && 'Gemini 2.5 Pro'}
                                  </span>
                                )}
                              </div>
                            </CardContent>
                          </Card>

                          {qualityReviewIndex && <Card>
                              <CardHeader>
                                <CardTitle className="flex items-center">
                                  <Star className="h-5 w-5 mr-2" />
                                  Indice de vérification (QRI)
                                </CardTitle>
                              </CardHeader>
                              <CardContent className="space-y-3">
                                <div className="flex items-center gap-2">
                                  <Badge className={getQriBadgeClass(qualityReviewIndex.level)}>
                                    {qualityReviewIndex.level || 'n/a'}
                                  </Badge>
                                  <span className="text-sm font-semibold">
                                    {qualityReviewIndex.score ?? 0}/100
                                  </span>
                                </div>
                                <div className="grid grid-cols-2 gap-3 text-xs text-muted-foreground">
                                  <div>Evidence coverage: {qualityReviewIndex.evidence_coverage_score ?? 'n/a'}</div>
                                  <div>Confidence: {qualityReviewIndex.confidence_score ?? 'n/a'}</div>
                                  <div>Consistency: {qualityReviewIndex.consistency_score ?? 'n/a'}</div>
                                  <div>Model agreement: {qualityReviewIndex.model_agreement_score ?? 'n/a'}</div>
                                </div>
                                {qualityReviewIndex.top_alerts && qualityReviewIndex.top_alerts.length > 0 && <div className="space-y-2">
                                    <div className="text-sm font-medium">Top alertes</div>
                                    <ul className="space-y-1">
                                      {qualityReviewIndex.top_alerts.map((alert: string, idx: number) => <li key={idx} className="text-xs flex items-start text-warning-foreground bg-warning/10 border border-warning/20 p-2 rounded">
                                          <AlertTriangle className="h-3 w-3 mr-2 mt-0.5 flex-shrink-0" />
                                          <span>{alert}</span>
                                        </li>)}
                                    </ul>
                                  </div>}
                              </CardContent>
                            </Card>}

                          <Card>
                            <CardHeader>
                              <CardTitle className="flex items-center">
                                <DollarSign className="h-5 w-5 mr-2" />
                                Coûts et tokens
                              </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-3">
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <div className="text-sm text-muted-foreground">Tokens d'entrée</div>
                                  <div className="font-medium">{analysis.tokens_in.toLocaleString()}</div>
                                </div>
                                <div>
                                  <div className="text-sm text-muted-foreground">Tokens de sortie</div>
                                  <div className="font-medium">{analysis.tokens_out.toLocaleString()}</div>
                                </div>
                                <div>
                                  <div className="text-sm text-muted-foreground">Total tokens</div>
                                  <div className="font-medium">{(analysis.tokens_in + analysis.tokens_out).toLocaleString()}</div>
                                </div>
                                {isFirstRun && (depotExtractionCost ?? 0) > 0 ? (
                                  <>
                                    <div>
                                      <div className="text-sm text-muted-foreground">Coût extraction</div>
                                      <div className="font-medium text-orange-500">CHF {(depotExtractionCost ?? 0).toFixed(4)}</div>
                                    </div>
                                    <div>
                                      <div className="text-sm text-muted-foreground">Coût analyse</div>
                                      <div className="font-medium">CHF {analysis.cost_estimate.toFixed(4)}</div>
                                    </div>
                                    <div className="col-span-2">
                                      <div className="text-sm text-muted-foreground">Coût total (extraction + analyse)</div>
                                      <div className="font-medium text-primary">CHF {((depotExtractionCost ?? 0) + analysis.cost_estimate).toFixed(4)}</div>
                                    </div>
                                  </>
                                ) : (
                                  <div>
                                    <div className="text-sm text-muted-foreground">Coût estimé</div>
                                    <div className="font-medium">CHF {analysis.cost_estimate.toFixed(4)}</div>
                                  </div>
                                )}
                              </div>
                            </CardContent>
                          </Card>

                          {/* Tracabilite des prompts */}
                          {analysis.resultat_json.analysis_metadata?.prompts_trace && <PromptTraceViewer promptsTrace={analysis.resultat_json.analysis_metadata.prompts_trace} />}

                          {analysis.resultat_json.analysis_metadata && <Card>
                              <CardHeader>
                                <CardTitle>Metadonnees d'analyse</CardTitle>
                              </CardHeader>
                              <CardContent>
                                <pre className="text-xs bg-muted/50 p-3 rounded-md overflow-auto">
                                  {JSON.stringify(analysis.resultat_json.analysis_metadata, null, 2)}
                                </pre>
                              </CardContent>
                            </Card>}
                        </div>
                      </ScrollArea>
                    </TabsContent>
                  </div>
                </Tabs>
              </div>
            </ResizablePanel>
          </ResizablePanelGroup>
        </div>

        {/* Actions Footer */}
        <div className="flex justify-end items-center space-x-2 px-6 py-4 border-t flex-shrink-0">
          <Button variant="outline" size="sm" onClick={handleExportPdf}>
            <FileDown className="h-4 w-4 mr-2" />
            PDF
          </Button>
          {onViewCV && <Button variant="outline" size="sm" onClick={() => onViewCV(analysis)}>
              <Eye className="h-4 w-4 mr-2" />
              Voir CV
            </Button>}
          {onReanalyze && <Button variant="outline" size="sm" onClick={() => onReanalyze(analysis.depot_id)} disabled={isReanalyzing}>
              {isReanalyzing ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
              Relancer
            </Button>}
          {onDelete && <Button variant="destructive" size="sm" onClick={() => onDelete(analysis.id)}>
              <Trash2 className="h-4 w-4 mr-2" />
              Supprimer
            </Button>}
        </div>
      </DialogContent>
    </Dialog>;
};